import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const TheEstatePage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { t } = useTranslation();

  const distilleries = [
    { name: 'Ardbeg', region: t('i18n.language') === 'en' ? 'South Islay' : 'Sul de Islay', desc: t('i18n.language') === 'en' ? 'Known for its intensely smoky and complex whiskies, with notes of peat, dark chocolate and spices.' : 'Conhecida por seus whiskies intensamente defumados e complexos, com notas de turfa, chocolate amargo e especiarias.' },
    { name: 'Laphroaig', region: t('i18n.language') === 'en' ? 'South Islay' : 'Sul de Islay', desc: t('i18n.language') === 'en' ? 'The most medicinal of Islay whiskies, with aromas of iodine, seaweed and a characteristic unmistakable smoke.' : 'O mais medicinal dos whiskies de Islay, com aromas de iodo, algas marinhas e uma fumaça característica inconfundível.' },
    { name: 'Lagavulin', region: t('i18n.language') === 'en' ? 'South Islay' : 'Sul de Islay', desc: t('i18n.language') === 'en' ? 'Elegant and deep, with 16 years of maturation that soften the peat and reveal notes of dried fruits and sherry.' : 'Elegante e profundo, com 16 anos de maturação que suavizam a turfa e revelam notas de frutas secas e xeres.' },
    { name: 'Bowmore', region: t('i18n.language') === 'en' ? 'Central Islay' : 'Centro de Islay', desc: t('i18n.language') === 'en' ? "Islay's oldest distillery, producing balanced whiskies with moderate peat and floral and fruity notes." : 'A destilaria mais antiga de Islay, produzindo whiskies equilibrados com turfa moderada e notas florais e frutadas.' },
    { name: 'Bruichladdich', region: t('i18n.language') === 'en' ? 'West Islay' : 'Oeste de Islay', desc: t('i18n.language') === 'en' ? 'Innovative and progressive, known for Port Charlotte and Octomore, the most heavily peated whisky in the world.' : 'Inovadora e progressista, conhecida pelo Port Charlotte e Octomore, o whisky mais defumado do mundo.' },
    { name: 'Caol Ila', region: t('i18n.language') === 'en' ? 'North Islay' : 'Norte de Islay', desc: t('i18n.language') === 'en' ? "Islay's largest distillery, producing a lighter, more floral whisky with gentle peat and refreshing citrus notes." : 'A maior destilaria de Islay, produzindo um whisky mais leve e floral, com turfa suave e notas cítricas refrescantes.' },
  ];

  return (
    <>
      <Navbar />
      <main className="bg-[#0d0d0d] min-h-screen">

        {/* Hero */}
        <section className="relative h-[65vh] min-h-[460px] flex items-end pb-20 px-6 overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://readdy.ai/api/search-image?query=Islay%20Scotland%20island%20dramatic%20coastline%20rugged%20cliffs%20Atlantic%20ocean%20waves%20moody%20overcast%20sky%20wild%20landscape%20peaty%20moorland%20cinematic%20wide%20angle%20photography%20dark%20atmospheric%20tones%20epic%20scenery&width=1600&height=900&seq=islay-hero1&orientation=landscape')" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-black/50 to-black/20" />
          <div className="relative z-10 max-w-4xl mx-auto w-full">
            <p className="text-amber-400/70 text-xs tracking-[0.3em] uppercase mb-3 font-light">{t('islay_about')}</p>
            <h1 className="text-5xl md:text-6xl font-light text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('islay_title')}</h1>
          </div>
        </section>

        {/* Intro */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-amber-400/70 text-xs tracking-[0.3em] uppercase mb-5 font-light">{t('islay_island')}</p>
                <h2 className="text-3xl md:text-4xl font-light text-white mb-8 leading-snug" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('islay_h2')}</h2>
                <p className="text-white/50 text-sm leading-relaxed mb-5 font-light">{t('islay_p1')}</p>
                <p className="text-white/50 text-sm leading-relaxed font-light">{t('islay_p2')}</p>
              </div>
              <div className="w-full h-80 lg:h-[420px] rounded-lg overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=Islay%20Scotland%20peat%20bog%20moorland%20golden%20hour%20sunset%20dramatic%20sky%20wild%20grass%20ancient%20landscape%20atmospheric%20moody%20photography%20warm%20amber%20tones%20remote%20island%20beauty&width=700&height=500&seq=islay-img1&orientation=landscape" alt="Islay landscape" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* Video */}
        <section className="py-10 px-6 bg-[#080808]">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-light text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('islay_video_title')}</h2>
            </div>
            <div className="w-full aspect-video rounded-lg overflow-hidden border border-white/10">
              <iframe title="Incredible Islay - Scotland Drone Series" src="https://www.youtube.com/embed/6o5TkSPKOJI" className="w-full h-full" frameBorder="0" allowFullScreen />
            </div>
          </div>
        </section>

        {/* Distilleries */}
        <section className="py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-amber-400/70 text-xs tracking-[0.3em] uppercase mb-4 font-light">{t('islay_distilleries_label')}</p>
              <h2 className="text-3xl md:text-4xl font-light text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('islay_distilleries_title')}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {distilleries.map(d => (
                <div key={d.name} className="border border-white/10 rounded-lg p-7 hover:border-amber-700/40 transition-colors group">
                  <p className="text-amber-400/50 text-xs tracking-widest uppercase mb-2 font-light">{d.region}</p>
                  <h3 className="text-white text-xl font-light mb-3 group-hover:text-amber-400/80 transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{d.name}</h3>
                  <p className="text-white/40 text-sm leading-relaxed font-light">{d.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Peat section */}
        <section className="py-0 bg-[#080808]">
          <div className="flex flex-col lg:flex-row min-h-[480px]">
            <div className="w-full lg:w-1/2 min-h-[320px] bg-cover bg-center" style={{ backgroundImage: "url('https://readdy.ai/api/search-image?query=peat%20cutting%20Scotland%20traditional%20craft%20dark%20earthy%20tones%20smoky%20atmosphere%20hands%20working%20ancient%20technique%20whisky%20production%20artisan%20heritage%20moody%20cinematic%20photography&width=800&height=600&seq=islay-peat1&orientation=landscape')" }} />
            <div className="w-full lg:w-1/2 flex flex-col justify-center px-10 md:px-16 py-16">
              <p className="text-amber-400/70 text-xs tracking-[0.3em] uppercase mb-5 font-light">{t('islay_peat_label')}</p>
              <h2 className="text-3xl font-light text-white mb-6 leading-snug" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('islay_peat_title')}</h2>
              <p className="text-white/50 text-sm leading-relaxed mb-5 font-light">{t('islay_peat_p1')}</p>
              <p className="text-white/50 text-sm leading-relaxed font-light">{t('islay_peat_p2')}</p>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
};

export default TheEstatePage;
